"use client";

import { useLayoutEffect } from "react";
import type { RefObject } from "react";

/**
 * Anchor a form→result swap BEFORE paint (rule 58).
 *
 * With `resultRef`, the result itself is scrolled to the top of the viewport
 * (its own scroll-margin clears the sticky header) and takes focus. Without it,
 * the page is anchored to the top. The ref form is the default for new
 * callers: top:0 put a phone back on the page hero with the result below the
 * fold and focus on <body> (C12, 2026-10-07).
 *
 * `useLayoutEffect`, never `useEffect`, and `behavior: "auto"`, never `"smooth"`:
 * the growth from a short form to a tall result lands hundreds of milliseconds
 * after the submit tap, so it is outside CLS's input-exclusion window and counts
 * in full. A post-paint effect runs after the shift has already painted — that
 * is exactly why the pre-2026-06-22 scroll fix never helped.
 *
 * Extracted so every matching mode shares ONE anchor rather than each result
 * surface growing its own near-copy (rules 23/24).
 */
export function useResultScrollAnchor(
  hasResult: boolean,
  resultRef?: RefObject<HTMLElement | null>
): void {
  useLayoutEffect(() => {
    if (typeof window === "undefined" || !hasResult) return;
    const result = resultRef?.current;
    if (result) {
      result.scrollIntoView?.({ block: "start", behavior: "auto" });
      result.focus({ preventScroll: true });
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [hasResult, resultRef]);
}
